import Header from '../../components/Header/Header';
import { Loader } from '@piggytechng/component-library';
import React, { useEffect, useState } from 'react';
import './WithdrawalManager.scss';
import useWithdrawalManager from '../../hooks/useWithdrawalManager';
import useWithdrawalActions from '../../hooks/useWithdrawalActions';
import { WithrawalDataType } from '../../types';
import { formatToNaira } from '../../helpers/utils';
import TransactionDetailsAlt from '../../components/NewSideSheet/TransactionDetailsAlt/TransactionDetailsAlt';
import ModalExecuteAction from '../../components/Modals/ModalExecuteAction';
import dayjs from 'dayjs';
import ModalSearch from '../../components/Modals/ModalSearch';
import NoData from '../../components/NoData/NoData';
import ModalFilter from '../../components/Modals/ModalFilter';
import useRolePermissions from '../../hooks/useRolePermissions';
import OptionDots from '../../assets/icons/option-dots.svg';
import { ADMIN_ROLES, localStorageEnum } from '../../api/enums';
import Pagination from '../../components/Pagination/Pagination';

const WithdrawalManager = () => {
  const { isLoadingRolesAndPermissions, restrictTabViewByRole } = useRolePermissions({});

  const [show, setShow] = useState(false);
  const [trDetails, setTrDetails] = useState<any>({});
  const [searchModal, setSearchModal] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [showExecuteAction, setShowExecuteAction] = useState(false);
  const [executionProps, setExecutionProps] = useState<{ text: string; actionColor: string }>({
    text: '',
    actionColor: '#1734CA',
  });
  const [showWithdrawalOptions, setShowWithdrawalOptions] = useState<string | null>();
  const { executeWithdrawalAction } = useWithdrawalActions();

  const {
    isLoading,
    withdrawalsList,
    withdrawalManagerParams,
    pagination,
    canResetSearch,
    canResetFilter,
    fetchData,
    goToNextPage,
    goToPrevPage,
    setLimit,
  } = useWithdrawalManager('normalWithdrawal');

  useEffect(() => {
    if (isLoadingRolesAndPermissions) return;
    restrictTabViewByRole('withdrawalManager', 'view', () => window.history.back());
  }, [isLoadingRolesAndPermissions]);

  const statusSetter = (
    bankCode: string,
    amount: number,
    statusCode: number, // There can only be 1, 3, or 0
    mode: string,
    transactionDate: number, // awhm_date
  ): string => {
    const normalizedMode = mode?.trim().toLowerCase();
    const normalizedBankCode = bankCode?.trim().toLowerCase();

    // 3 is reversed regardless of mode
    if (statusCode === 3) {
      return 'Reversed';
    }

    // 1 is successful regardless of mode
    if (statusCode === 1) {
      return 'Success';
    }

    // Anything besides 1, 3, and 0 is unknown and requires investigation
    if (statusCode !== 0) {
      return 'Unknown';
    }

    // If execution gets here, the status code is 0
    let requiresApproval = false;

    if (normalizedMode !== 'initiated' && normalizedBankCode === 'abeg' && amount > 3000000) {
      requiresApproval = true;
    }

    if (normalizedMode !== 'initiated' && normalizedBankCode !== 'abeg' && amount > 5100000) {
      requiresApproval = true;
    }

    if (requiresApproval) {
      return 'On Hold';
    }

    // Status is 0, but the mode could be Initiated, Paystack, Patronize, or Pocket. If the transaction is older than
    // 15 minutes, its status should be confirmed with the provider because it could have been processed but updating the
    // status on DB failed. Lets show confirm from provider to the admin
    const hasValidTransactionDate = Number.isFinite(transactionDate) && transactionDate > 0;

    if (!hasValidTransactionDate) {
      return 'Check status from processor';
    }

    const transactionTime = dayjs.unix(transactionDate);
    const minutesSinceTransaction = dayjs().diff(transactionTime, 'minute');

    if (minutesSinceTransaction >= 15) {
      return 'Check processor status';
    }

    return 'Pending';
  };

  const formatWithdrawalData = (val: WithrawalDataType) => {
    const d = new Date(val?.awhm_date * 1000);
    const date = dayjs(d).format('D MMM YYYY, h:mm A');

    const bankCode = val?.awhm_bankcode;
    const amount = val?.awhm_amount;
    const statusCode = val?.awhm_status;
    const mode = val?.awhm_mode;

    const status = statusSetter(bankCode, amount, statusCode, mode, val?.awhm_date);

    return { date, status, mode, amount };
  };

  const handleViewSingleTransaction = (singleData: WithrawalDataType) => {
    const formatedData = {
      'Bank Code': singleData?.awhm_bankcode,
      amount: formatToNaira(singleData?.awhm_amount),
      status: statusSetter(
        singleData.awhm_bankcode,
        singleData.awhm_amount,
        singleData.awhm_status,
        singleData.awhm_mode,
        singleData?.awhm_date,
      ),
      mode: singleData?.awhm_mode,
      'Agent ID': singleData.awhm_agent_id,
      'Account Number': singleData.awhm_accountnumber,
      'Bank Name': singleData.bankName,
      Penalty: formatToNaira(singleData.awhm_penalty),
      Reference: singleData.awhm_reference,
      'Withdrawal ID': singleData.awhm_rand,
      Date: dayjs(new Date(singleData.awhm_date * 1000)).format('D MMM YYYY, h:mm A'),
      Email: singleData.email,
    };

    setTrDetails(formatedData);
    setShow(true);
  };

  const clearFilter = () => {
    fetchData();
  };

  const runAction = (action: string, data: any) => {
    const withdrawal = data.awhm_rand ? data : { awhm_rand: data['Withdrawal ID'], awhm_agent_id: data['Agent ID'] };

    setExecutionProps({
      text: action === 'reverse' ? 'reverse this transaction' : 'retry this transaction',
      actionColor: '#1734CA',
    });

    setTrDetails(withdrawal);
    setShowExecuteAction(true);

    withdrawalActionRef.current = { action, withdrawal };
  };

  const withdrawalActionRef = React.useRef<{ action: string; withdrawal: any }>({ action: '', withdrawal: null });

  const handleExecuteAction = (password: string) => {
    const { action, withdrawal } = withdrawalActionRef.current;
    executeWithdrawalAction(action, withdrawal, password, () => {
      setShowExecuteAction(false);
      setShow(false);
      fetchData();
    });
  };

  const adminRole = localStorage.getItem(localStorageEnum['local.storage.role']);

  return (
    <>
      <Header name="Withdrawal Manager" />
      <div className="withdrawal-manager">
        <div className="withdrawal-manager__card">
          {isLoading ? (
            <div className="page-loader">
              <Loader color="#0F60D6" />
            </div>
          ) : (
            <>
              <div className="customers-filter">
                <div className="input-field" onClick={() => setSearchModal(true)}>
                  <input type="text" placeholder="Search" value={withdrawalManagerParams.query} />
                </div>
                <div className="query-filter" onClick={() => setFilterModal(true)}>
                  <div>
                    <svg width="10" height="10" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M0.75 0.75H17.25M6.75 13.25H11.25M3.75 7H14.25"
                        stroke="#1E293B"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                      />
                    </svg>
                  </div>
                  <div>Filter</div>
                </div>

                {(canResetSearch || canResetFilter) && (
                  <div className="query-filter" onClick={() => clearFilter()}>
                    <div></div>
                    <div>Reset</div>
                  </div>
                )}
              </div>
              {withdrawalsList && withdrawalsList.length === 0 ? (
                <NoData
                  message={canResetFilter || canResetSearch ? 'No withdrawals found' : 'No withdrawals at the moment'}
                />
              ) : (
                <div className="table-container withdrawal">
                  <table className="table">
                    <thead className="table_head">
                      <tr>
                        <th className="wth-id">Withdrawal ID</th>
                        <th className="wth-email">Email</th>
                        <th className="wth-amount">{`Amount(₦)`}</th>
                        <th className="wth-account">Account Info</th>
                        <th className="wth-status">Status</th>
                        <th className="wth-processor">Processor</th>
                        <th className="wth-date">Date</th>
                        <th className="wth-action">Action</th>
                        <th className="wth-dots">{''}</th>
                      </tr>
                    </thead>

                    <tbody className="table-body">
                      {withdrawalsList?.map((val: WithrawalDataType, index: any) => {
                        const { date, status } = formatWithdrawalData(val);
                        const largeFailed =
                          (val.awhm_bankcode === 'abeg' && val.awhm_amount >= 3000000) ||
                          (val.awhm_bankcode !== 'abeg' && val.awhm_amount >= 5000000);

                        const hideRetryLargeFailed = adminRole !== (ADMIN_ROLES.super_admin as string) && largeFailed;

                        return (
                          <tr
                            key={index}
                            id={val.awhm_id}
                            className="table-row"
                            onClick={(e: any) => {
                              if (!e.target.className.includes('wth-option')) handleViewSingleTransaction(val);
                            }}
                          >
                            <td className="wth-id" title={val.awhm_rand}>
                              {val.awhm_rand}
                            </td>
                            <td className="wth-email" title={val.email}>
                              {val.email}
                            </td>
                            <td className="wth-amount">{formatToNaira(val.awhm_amount)}</td>
                            <td className="wth-account account-info">
                              <p>{val.bankName ? val.bankName : val.awhm_bankcode}</p>
                              <p>{val.awhm_accountnumber}</p>
                            </td>
                            <td className="wth-status">
                              <p className={`mode ${status?.replace(' ', '-')}`}>{status}</p>
                            </td>
                            <td className="wth-processor">{val.awhm_mode}</td>
                            <td className="wth-date">{date}</td>
                            <td className="wth-action">
                              <p className="action">View</p>
                            </td>
                            <div className="wth-dots">
                              {!['Success', 'Reversed'].includes(status) && !hideRetryLargeFailed && (
                                <img
                                  className="wth-option-dots"
                                  width={15}
                                  height={15}
                                  src={OptionDots}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowWithdrawalOptions(
                                      showWithdrawalOptions === val.awhm_id ? null : val.awhm_id,
                                    );
                                  }}
                                />
                              )}
                              {showWithdrawalOptions === val.awhm_id && (
                                <div
                                  className="wth-options"
                                  onMouseLeave={() => {
                                    setTimeout(() => {
                                      setShowWithdrawalOptions(null);
                                    }, 200);
                                  }}
                                >
                                  {['Pending'].includes(status) && (
                                    <div className="wth-option" onClick={() => runAction('reverse', val)}>
                                      <div className="wth-option-icon" style={{ backgroundColor: '#16A34A' }}></div>
                                      <p className="wth-option-text">Reverse Transaction</p>
                                    </div>
                                  )}

                                  {['Pending'].includes(status) && (
                                    <div className="wth-option" onClick={() => runAction('retry', val)}>
                                      <div className="wth-option-icon" style={{ backgroundColor: '#D98D01' }}></div>
                                      <p className="wth-option-text">Retry Transaction</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {(pagination.hasNextPage || pagination.hasPrevPage) && (
                <Pagination
                  currentPage={pagination.page}
                  hasNextPage={pagination.hasNextPage}
                  hasPrevPage={pagination.hasPrevPage}
                  onNextPage={goToNextPage}
                  onPrevPage={goToPrevPage}
                  limit={pagination.pageSize}
                  onLimitChange={setLimit}
                />
              )}
            </>
          )}

          <TransactionDetailsAlt
            show={show}
            id={''}
            data={trDetails}
            which="withdrawal"
            runAction={runAction}
            onClose={() => {
              setShow(false);
            }}
          />

          <ModalExecuteAction
            modalIsOpen={showExecuteAction}
            setIsOpen={setShowExecuteAction}
            text={executionProps.text}
            actionColor={executionProps.actionColor}
            action={handleExecuteAction}
            isLoading={false}
          />

          <ModalSearch
            modalIsOpen={searchModal}
            searchContext="search-withdrawal-manager"
            data={withdrawalManagerParams}
            setIsOpen={setSearchModal}
            searchDataBy={(filter, empty) => {
              const params = {
                ...withdrawalManagerParams,
                startDate: '',
                endDate: '',
                statusFilter: '',
                modeFilter: '',
                ...filter.withdrawalManager,
              };
              if (!empty) {
                fetchData(params);
              }
            }}
          />

          <ModalFilter
            modalIsOpen={filterModal}
            searchContext="filter-withdrawal-manager"
            data={withdrawalManagerParams}
            setIsOpen={setFilterModal}
            filterDataBy={(filter, empty) => {
              const params = {
                ...withdrawalManagerParams,
                query: '',
                queryFilter: '',
                ...filter.withdrawalManager,
              };
              if (!empty) {
                fetchData(params);
              }
            }}
          />
        </div>
      </div>
    </>
  );
};

export default WithdrawalManager;
